import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { Evento, TipoEvento } from "../../../data/portal-data";
import "../../../pages/portal/components/inicio/eventos.scss";
import { portalEventosServices } from "../../../services/portal-eventos.services";
import { getFormattedDate } from "../../../utils/common.utils";
import "./web-eventos.scss";

/** Carga el CSS de Bootstrap Icons si no está ya en el documento */
function useBootstrapIcons() {
  useEffect(() => {
    const href = "/assets/portal/vendor/bootstrap-icons/bootstrap-icons.css";
    if (!document.querySelector(`link[href="${href}"]`)) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = href;
      document.head.appendChild(link);
    }
  }, []);
}

const COLORES = {
  cyan: {
    border: "#06b6d4",
    bg: "linear-gradient(135deg,#06b6d4,#0891b2)",
    tipo_bg: "rgba(6,182,212,0.1)",
    tipo_color: "#0891b2",
  },
  green: {
    border: "#10b981",
    bg: "linear-gradient(135deg,#10b981,#059669)",
    tipo_bg: "rgba(16,185,129,0.1)",
    tipo_color: "#059669",
  },
  amber: {
    border: "#f59e0b",
    bg: "linear-gradient(135deg,#f59e0b,#d97706)",
    tipo_bg: "rgba(245,158,11,0.1)",
    tipo_color: "#d97706",
  },
};

const PORTAL_TIPO_CONFIG: Record<
  string,
  { label: string; biIcon: string; color: "cyan" | "green" | "amber" }
> = {
  CURSO: { label: "Curso", biIcon: "bi-mortarboard-fill", color: "cyan" },
  EVENTO: { label: "Evento", biIcon: "bi-calendar-event-fill", color: "green" },
  NOVEDAD: { label: "Novedad", biIcon: "bi-megaphone-fill", color: "amber" },
};

const TIPO_CONFIG: Record<
  string,
  {
    label: string;
    color:
      | "default"
      | "primary"
      | "warning"
      | "success"
      | "error"
      | "info"
      | "secondary";
  }
> = {
  CURSO: { label: "Curso", color: "info" },
  EVENTO: { label: "Evento", color: "success" },
  NOVEDAD: { label: "Novedad", color: "warning" },
};

const DEFAULT_PORTAL_CFG = {
  label: "Evento",
  biIcon: "bi-calendar-event-fill",
  color: "green" as const,
};
const DEFAULT_CHIP_CFG = { label: "Evento", color: "default" as const };

/** Normaliza el tipo que llega del backend a mayúsculas */
function normalizarTipo(tipo: string): string {
  return tipo?.toUpperCase() ?? "EVENTO";
}

const hoy = new Date().toISOString().split("T")[0];

const eventoVacio: Omit<Evento, "id"> = {
  tipo: "NOVEDAD",
  titulo: "",
  descripcion: "",
  fecha: "",
  hora: "",
  lugar: "",
  badge: "",
  vigenciaDesde: hoy,
  vigenciaHasta: "",
};

function esVisible(ev: Evento): boolean {
  const hoyStr = new Date().toISOString().split("T")[0];
  const desde = ev.vigenciaDesde
    ? ev.vigenciaDesde.toString().slice(0, 10)
    : "0000-01-01";
  const hasta = ev.vigenciaHasta
    ? ev.vigenciaHasta.toString().slice(0, 10)
    : "9999-12-31";
  return hoyStr >= desde && hoyStr <= hasta;
}

export default function WebEventos() {
  useBootstrapIcons();
  const [eventos, setEventos] = useState<Evento[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editando, setEditando] = useState<Evento | null>(null);
  const [form, setForm] = useState<Omit<Evento, "id">>(eventoVacio);
  const [errors, setErrors] = useState<Partial<Record<keyof Evento, string>>>(
    {}
  );
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [alertMsg, setAlertMsg] = useState<{
    text: string;
    severity: "success" | "error";
  } | null>(null);
  const [previewEvento, setPreviewEvento] = useState<Evento | null>(null);

  useEffect(() => {
    portalEventosServices
      .getAll()
      .then((res) => setEventos(res.data))
      .catch(() =>
        setAlertMsg({ text: "Error al cargar los eventos", severity: "error" })
      )
      .finally(() => setLoading(false));
  }, []);

  const handleNuevo = () => {
    setEditando(null);
    setForm(eventoVacio);
    setErrors({});
    setDialogOpen(true);
  };

  /** Extrae solo "YYYY-MM-DD" de un string ISO, o devuelve "" si es null/undefined */
  const soloFecha = (f?: string | null): string =>
    f ? f.toString().slice(0, 10) : "";

  const handleEditar = async (ev: Evento) => {
    try {
      const res = await portalEventosServices.getById(ev.id);
      const data = res.data;
      setEditando(data);
      setForm({
        ...data,
        fecha: soloFecha(data.fecha),
        vigenciaDesde: soloFecha(data.vigenciaDesde),
        vigenciaHasta: soloFecha(data.vigenciaHasta),
      });
      setErrors({});
      setDialogOpen(true);
    } catch {
      setAlertMsg({ text: "Error al cargar el evento", severity: "error" });
    }
  };

  const handlePreview = async (ev: Evento) => {
    try {
      const res = await portalEventosServices.getById(ev.id);
      setPreviewEvento(res.data);
    } catch {
      setAlertMsg({ text: "Error al cargar el evento", severity: "error" });
    }
  };

  const validar = (): boolean => {
    const errs: Partial<Record<keyof Evento, string>> = {};
    if (!form.titulo.trim()) errs.titulo = "El título es obligatorio";
    if (!form.descripcion.trim())
      errs.descripcion = "La descripción es obligatoria";
    if (!form.vigenciaDesde)
      errs.vigenciaDesde = "La fecha de inicio de publicación es obligatoria";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // Convierte strings vacíos a null en campos de fecha opcionales
  const sanitizarFechas = (data: Omit<Evento, "id">) => ({
    ...data,
    fecha: data.fecha || null,
    hora: data.hora || null,
    lugar: data.lugar || null,
    badge: data.badge || null,
    vigenciaHasta: data.vigenciaHasta || null,
  });

  const handleGuardar = async () => {
    if (!validar()) return;
    const payload = sanitizarFechas(form);
    try {
      if (editando) {
        const res = await portalEventosServices.update(editando.id, {
          ...payload,
          id: editando.id,
        });
        setEventos((prev) =>
          prev.map((e) => (e.id === editando.id ? res.data : e))
        );
        setAlertMsg({
          text: "Evento actualizado correctamente",
          severity: "success",
        });
      } else {
        const res = await portalEventosServices.create(payload);
        setEventos((prev) => [...prev, res.data]);
        setAlertMsg({
          text: "Evento creado correctamente",
          severity: "success",
        });
      }
      setDialogOpen(false);
    } catch {
      setAlertMsg({ text: "Error al guardar el evento", severity: "error" });
    }
  };

  const handleEliminar = async (id: number) => {
    try {
      await portalEventosServices.delete(id);
      setEventos((prev) => prev.filter((e) => e.id !== id));
      setAlertMsg({ text: "Evento eliminado", severity: "success" });
    } catch {
      setAlertMsg({ text: "Error al eliminar el evento", severity: "error" });
    }
    setConfirmDeleteId(null);
  };

  return (
    <div className="web-eventos">
      <div className="header-section">
        <div>
          <h1>Cursos, Eventos y Novedades</h1>
          <p>Administre los eventos que se muestran en el portal público</p>
        </div>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleNuevo}
        >
          Nuevo
        </Button>
      </div>

      {alertMsg && (
        <Alert
          severity={alertMsg.severity}
          onClose={() => setAlertMsg(null)}
          sx={{ mb: 2 }}
        >
          {alertMsg.text}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <div className="eventos-table">
          <div className="table-header">
            <span>Tipo</span>
            <span>Título</span>
            <span>Fecha evento</span>
            <span>Publicación</span>
            <span>Estado</span>
            <span>Acciones</span>
          </div>

          {eventos.length === 0 && (
            <div className="table-empty">No hay eventos cargados</div>
          )}

          {eventos.map((ev) => (
            <div
              key={ev.id}
              className={`table-row${esVisible(ev) ? "" : " row--inactivo"}`}
            >
              <span>
                <Chip
                  label={
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 4,
                      }}
                    >
                      <i
                        className={`bi ${(PORTAL_TIPO_CONFIG[normalizarTipo(ev.tipo)] ?? DEFAULT_PORTAL_CFG).biIcon}`}
                      ></i>
                      {
                        (
                          TIPO_CONFIG[normalizarTipo(ev.tipo)] ??
                          DEFAULT_CHIP_CFG
                        ).label
                      }
                    </span>
                  }
                  color={
                    (TIPO_CONFIG[normalizarTipo(ev.tipo)] ?? DEFAULT_CHIP_CFG)
                      .color
                  }
                  size="small"
                />
              </span>
              <span className="col-titulo">
                <strong>{ev.titulo}</strong>
                {ev.badge && (
                  <Chip
                    label={ev.badge}
                    size="small"
                    variant="outlined"
                    sx={{ ml: 1 }}
                  />
                )}
                <Typography
                  variant="caption"
                  display="block"
                  color="text.secondary"
                  noWrap
                >
                  {ev.descripcion}
                </Typography>
              </span>
              <span className="col-fecha">
                {getFormattedDate(ev.fecha ? ev.fecha.toString() : "")}
              </span>
              <span className="col-publicacion">
                <Typography variant="caption" display="block">
                  Desde:{" "}
                  {getFormattedDate(
                    ev.vigenciaDesde ? ev.vigenciaDesde.toString() : ""
                  ) ?? "—"}
                </Typography>
                <Typography variant="caption" display="block">
                  Hasta:{" "}
                  {getFormattedDate(
                    ev.vigenciaHasta ? ev.vigenciaHasta.toString() : ""
                  ) ?? "sin límite"}
                </Typography>
              </span>
              <span>
                <Chip
                  label={esVisible(ev) ? "Visible" : "Oculto"}
                  color={esVisible(ev) ? "success" : "default"}
                  size="small"
                  variant="outlined"
                />
              </span>
              <span className="col-acciones">
                <Tooltip title="Vista previa">
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => handlePreview(ev)}
                  >
                    <VisibilityOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Editar">
                  <IconButton size="small" onClick={() => handleEditar(ev)}>
                    <EditOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Eliminar">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => setConfirmDeleteId(ev.id)}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Diálogo crear/editar */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{editando ? "Editar Evento" : "Nuevo Evento"}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Tipo</InputLabel>
              <Select
                value={form.tipo}
                label="Tipo"
                onChange={(e) =>
                  setForm({ ...form, tipo: e.target.value as TipoEvento })
                }
              >
                <MenuItem value="CURSO">Curso</MenuItem>
                <MenuItem value="EVENTO">Evento</MenuItem>
                <MenuItem value="NOVEDAD">Novedad</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Título"
              value={form.titulo}
              onChange={(e) => setForm({ ...form, titulo: e.target.value })}
              error={!!errors.titulo}
              helperText={errors.titulo}
              fullWidth
              required
            />

            <TextField
              label="Descripción"
              value={form.descripcion}
              onChange={(e) =>
                setForm({ ...form, descripcion: e.target.value })
              }
              error={!!errors.descripcion}
              helperText={errors.descripcion}
              fullWidth
              required
              multiline
              rows={3}
            />

            <TextField
              label="Fecha del evento (opcional)"
              type="date"
              value={form.fecha ?? ""}
              onChange={(e) =>
                setForm({ ...form, fecha: e.target.value || undefined })
              }
              error={!!errors.fecha}
              helperText={errors.fecha}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />

            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                label="Hora (opcional)"
                value={form.hora ?? ""}
                onChange={(e) => setForm({ ...form, hora: e.target.value })}
                fullWidth
                placeholder="ej: 10:00 hs"
              />
              <TextField
                label="Badge (opcional)"
                value={form.badge ?? ""}
                onChange={(e) => setForm({ ...form, badge: e.target.value })}
                fullWidth
                placeholder="ej: Gratuito"
              />
            </Box>

            <TextField
              label="Lugar (opcional)"
              value={form.lugar ?? ""}
              onChange={(e) => setForm({ ...form, lugar: e.target.value })}
              fullWidth
            />

            <Typography
              variant="subtitle2"
              sx={{ mt: 1, mb: 1, color: "text.secondary" }}
            >
              Período de publicación en el portal
            </Typography>
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                label="Publicar desde"
                type="date"
                value={form.vigenciaDesde ?? ""}
                onChange={(e) =>
                  setForm({ ...form, vigenciaDesde: e.target.value })
                }
                error={!!errors.vigenciaDesde}
                helperText={
                  errors.vigenciaDesde ?? "Fecha en que aparece en el portal"
                }
                fullWidth
                required
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Publicar hasta"
                type="date"
                value={form.vigenciaHasta ?? ""}
                onChange={(e) =>
                  setForm({ ...form, vigenciaHasta: e.target.value })
                }
                helperText="Dejar vacío para sin fecha límite"
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleGuardar}>
            {editando ? "Guardar cambios" : "Crear"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo vista previa */}
      {previewEvento &&
        (() => {
          const cfg =
            PORTAL_TIPO_CONFIG[normalizarTipo(previewEvento.tipo)] ??
            DEFAULT_PORTAL_CFG;
          const c = COLORES[cfg.color];
          const [y, m, d] = previewEvento.fecha
            ? previewEvento.fecha.toString().slice(0, 10).split("-")
            : ["—", "—", "—"];
          const meses = [
            "ene",
            "feb",
            "mar",
            "abr",
            "may",
            "jun",
            "jul",
            "ago",
            "sep",
            "oct",
            "nov",
            "dic",
          ];
          const mes = m && m !== "—" ? meses[parseInt(m) - 1] : "—";
          return (
            <Dialog
              open
              onClose={() => setPreviewEvento(null)}
              maxWidth="xs"
              fullWidth
            >
              <DialogTitle
                sx={{ pb: 0, fontSize: "0.95rem", color: "#64748b" }}
              >
                Vista previa — tal como se verá en el portal
              </DialogTitle>
              <DialogContent sx={{ pt: 2, background: "#f8fafc" }}>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: "16px",
                    background: "white",
                    border: `1px solid #e2e8f0`,
                    borderTop: `4px solid ${c.border}`,
                    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                    overflow: "hidden",
                    minHeight: "395px",
                  }}
                >
                  {/* Header coloreado — siempre visible */}
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "100px",
                      minHeight: "100px",
                      padding: "0 24px",
                      background: c.bg,
                    }}
                  >
                    {previewEvento.fecha ? (
                      <>
                        <span
                          style={{
                            fontFamily: "Poppins,sans-serif",
                            fontSize: "2.4rem",
                            fontWeight: 700,
                            color: "white",
                            lineHeight: 1,
                          }}
                        >
                          {d}
                        </span>
                        <span
                          style={{
                            fontSize: "0.9rem",
                            fontWeight: 600,
                            color: "rgba(255,255,255,0.85)",
                            textTransform: "uppercase",
                            letterSpacing: "1px",
                          }}
                        >
                          {mes}
                        </span>
                        <span
                          style={{
                            fontSize: "0.78rem",
                            color: "rgba(255,255,255,0.65)",
                            marginTop: "2px",
                          }}
                        >
                          {y}
                        </span>
                      </>
                    ) : (
                      <i
                        className={`bi ${cfg.biIcon}`}
                        style={{
                          fontSize: "2rem",
                          color: "white",
                          opacity: 0.9,
                        }}
                      ></i>
                    )}
                  </div>
                  {/* Body */}
                  <div
                    style={{
                      padding: "20px 24px 24px",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        marginBottom: "12px",
                        flexWrap: "wrap",
                      }}
                    >
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "5px",
                          fontSize: "0.75rem",
                          fontWeight: 700,
                          fontFamily: "Poppins,sans-serif",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          padding: "3px 10px",
                          borderRadius: "50px",
                          background: c.tipo_bg,
                          color: c.tipo_color,
                        }}
                      >
                        <i
                          className={`bi ${cfg.biIcon}`}
                          style={{ fontSize: "0.85em" }}
                        ></i>{" "}
                        {cfg.label}
                      </span>
                      {previewEvento.badge && (
                        <span
                          style={{
                            fontSize: "0.72rem",
                            fontWeight: 600,
                            padding: "3px 10px",
                            borderRadius: "50px",
                            background: "#fee2e2",
                            color: "#dc2626",
                          }}
                        >
                          {previewEvento.badge}
                        </span>
                      )}
                    </div>
                    <h4
                      style={{
                        fontFamily: "Poppins,sans-serif",
                        fontSize: "1rem",
                        fontWeight: 700,
                        color: "#1e293b",
                        margin: "0 0 10px",
                        lineHeight: 1.4,
                      }}
                    >
                      {previewEvento.titulo}
                    </h4>
                    <p
                      style={{
                        fontSize: "0.88rem",
                        color: "#64748b",
                        lineHeight: 1.6,
                        margin: 0,
                      }}
                    >
                      {previewEvento.descripcion}
                    </p>
                    {(previewEvento.hora || previewEvento.lugar) && (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "5px",
                          marginTop: "16px",
                          paddingTop: "14px",
                          borderTop: "1px solid #f1f5f9",
                        }}
                      >
                        {previewEvento.hora && (
                          <span
                            style={{
                              fontSize: "0.82rem",
                              color: "#475569",
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                            }}
                          >
                            🕐 {previewEvento.hora}
                          </span>
                        )}
                        {previewEvento.lugar && (
                          <span
                            style={{
                              fontSize: "0.82rem",
                              color: "#475569",
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                            }}
                          >
                            📍 {previewEvento.lugar}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setPreviewEvento(null)}>Cerrar</Button>
              </DialogActions>
            </Dialog>
          );
        })()}

      {/* Diálogo confirmar eliminación */}
      <Dialog
        open={confirmDeleteId !== null}
        onClose={() => setConfirmDeleteId(null)}
      >
        <DialogTitle>Confirmar eliminación</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Está seguro que desea eliminar este evento? Esta acción no se puede
            deshacer.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteId(null)}>Cancelar</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => handleEliminar(confirmDeleteId!)}
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
